import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, User, ChevronDown, Menu, Search } from 'lucide-react';
import { NotificationBell } from '@/components/notifications';
import { getInitials } from '@/lib/utils';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { HelpLink } from '@/components/help/HelpLink';
import { SidebarNav } from './Sidebar';

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setIsOpen(false);

    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleProfile = () => {
    setIsOpen(false);
    navigate('/settings');
  };

  return (
    <header className="fixed top-0 left-0 lg:left-64 right-0 h-16 border-b border-white/10 flex items-center justify-between px-4 lg:px-6 z-10">
      {/* Glass backdrop on a childless layer: backdrop-filter on the header
       * itself would become the containing block for the user-menu's
       * fixed inset-0 click-catcher and break click-outside-to-close. */}
      <div aria-hidden className="absolute inset-0 -z-10 bg-bg-secondary/75 backdrop-blur-xl" />
      {/* Left: Mobile menu + Org Name */}
      <div className="flex items-center gap-3">
        {/* Mobile hamburger */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <button className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-text-secondary">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <div className="flex flex-col h-full text-text-primary">
              <SidebarNav onNavigate={() => setMobileMenuOpen(false)} />
            </div>
          </SheetContent>
        </Sheet>

        <h2 className="text-lg font-semibold text-text-primary truncate">
          {user?.organization.name}
        </h2>
      </div>

      {/* Right: Search hint + User Menu */}
      <div className="flex items-center gap-2">
        {/* Search shortcut hint */}
        <button
          onClick={() => {
            window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
          }}
          className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 text-text-tertiary text-sm hover:bg-white/5 transition-colors"
        >
          <Search className="h-4 w-4" />
          <span>Search...</span>
          <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-white/10 rounded border border-white/10 shadow-[inset_0_-1px_0_rgba(0,0,0,0.4)]">⌘K</kbd>
        </button>

        {/* Role-aware product guide */}
        {user && <HelpLink audience={user.role} />}

        {/* Notifications */}
        <NotificationBell />

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors outline-none"
          >
            <div className="w-8 h-8 rounded-full bg-emerald-900/50 text-emerald-400 flex items-center justify-center text-sm font-medium">
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(user?.firstName, user?.lastName)
              )}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-sm font-medium text-text-primary">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-text-tertiary capitalize">
                {user?.role.replace('_', ' ')}
              </p>
            </div>
            <ChevronDown className={`h-4 w-4 text-text-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-48 rounded-lg border border-white/10 bg-bg-elevated shadow-overlay z-50 animate-in fade-in-0 zoom-in-95 duration-100">
                <div className="py-1">
                  <button
                    onClick={handleProfile}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-white/5 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </button>

                  {user && (
                    <HelpLink
                      audience={user.role}
                      variant="menu"
                      className="sm:hidden"
                      onClick={() => setIsOpen(false)}
                    />
                  )}

                  <div className="my-1 h-px bg-white/10" />

                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                  >
                    <LogOut className="h-4 w-4" />
                    {isLoggingOut ? 'Signing out...' : 'Sign out'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
